import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '@/lib/query-keys';
import type { SetShiftOverrideInput, Shift } from '../types';

interface SetShiftOverrideVariables extends SetShiftOverrideInput {
  practitionerId: string;
}

interface UseSetShiftOverrideOptions {
  onSuccess?: (shifts: Shift[]) => void;
  onError?: (error: Error) => void;
}

export const useSetShiftOverride = (options?: UseSetShiftOverrideOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ practitionerId, ...input }: SetShiftOverrideVariables) =>
      apiClient.put<Shift[]>(`shifts/override/${practitionerId}`, input),
    onSuccess: (shifts) => {
      void queryClient.invalidateQueries({ queryKey: ['shifts'] });
      // A shift outside opening hours can open the location for its date.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.locationOpeningHours.all(),
      });
      toast.success('Shift updated');
      options?.onSuccess?.(shifts);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update shift');
      options?.onError?.(error);
    },
  });

  return {
    setShiftOverride: mutation.mutate,
    setShiftOverrideAsync: mutation.mutateAsync,
    isSaving: mutation.isPending,
  };
};
